import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { OverlayPanel } from 'primeng/overlaypanel';
import { ConfirmDialogEventType, ConfirmDialogSeverity } from '../components/confirmDialog/confirmDialogComponent';
import { ConfirmDialogService } from '../components/confirmDialog/confirmDialogService';
import { EditDialogService } from '../components/editDialog/editDialogService';
import { TimeEntryDialogDialogComponent } from '../components/time-entry-dialog/time-entry-dialog.component';
import { TimeEntry } from '../shared/entities/timeEntry/timeEntry';
import { TimeEntryService } from '../shared/services/timeEntry/timeEntryService';

export interface Task {
    id: string;
    timeEntriesSum: number;
    timeEntries: TimeEntry[]
}
@Component({
    selector: 'nx-edit-time-entries-input',
    template: `
    <div class="nx-edit-time-entries-input">
        <label
            *ngIf="showLabel"
            for="textarea">{{label}}</label>
            <div class="flex flex-row" (click)="$event.stopPropagation()">
            <div
                class="input previewValue"
                (click)="toggleOverlayPanel($event)"
                [ngClass]="{'focus': overlayPanelVisible}">
                <ng-container
                    *ngIf="this.buildTimeEntryString()">
                    {{this.buildTimeEntryString()}}
                </ng-container>
                <span class="placeholder"
                    *ngIf="!this.buildTimeEntryString()">
                    {{placeholder}}
                </span>
            </div>
            
            <p-overlayPanel
                [dismissable]="isOverlayPanelDismissable"
                [style]="{'width':'740px'}"
                #overlayPanel>
                <div class="flex flex-col nx-edit-time-entries-input-overlay">
                    <div>
                        <div
                            *ngIf="task.timeEntries == undefined || task.timeEntries.length == 0" 
                            class="text-base text-[#848488] font-light text-center">
                            Keine Zeiteinträge vorhanden.
                        </div>
                        <table
                            *ngIf="task.timeEntries && task.timeEntries.length > 0"
                            class="time-entries-table">
                            <thead class="time-entries-table">
                                <tr>
                                    <th>Start</th>
                                    <th>Ende</th>
                                    <th>Pause</th>
                                    <th>Dauer</th>
                                    <th>Kategorie</th>
                                    <th>Nutzer</th>
                                    <th class="button"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let timeEntry of task.timeEntries">
                                    <td class="text">{{timeEntry.startDateTime | date:'dd.MM.yy HH:mm'}}</td>
                                    <td class="text">{{timeEntry.endDateTime | date:'dd.MM.yy HH:mm'}}</td>
                                    <td class="text">{{timeEntry.pauseDuration | secondsToDuration}}</td>
                                    <td class="text">{{timeEntry.duration | secondsToDuration}}</td>
                                    <td class="text">{{timeEntry.category?.name}}</td>
                                    <td class="text">{{timeEntry.user?.firstName + ' ' + timeEntry?.user?.lastName}}</td>
                                    <td class="button">
                                        <nx-default-button
                                            *ngIf="timeEntry?.note"
                                            class="mr-1"
                                            [clickable]="false"
                                            [style]="'light'"
                                            [icon]="'icon-info'"
                                            [tooltip]="timeEntry?.note ? timeEntry?.note : undefined"
                                            (click)="$event.stopPropagation();"
                                            [ngClass]="timeEntry?.note ? 'note' :'no-note'">
                                        </nx-default-button>
                                        <nx-default-button
                                            class="mr-1"
                                            [style]="'dark'"
                                            (onClick)="deleteTimeEntry(timeEntry)"
                                            [tooltip]="'Zeiteintrag löschen'"
                                            [icon]="'icon-trash'">
                                        </nx-default-button>   
                                        <nx-default-button
                                            [style]="'dark'"
                                            (onClick)="updateTimeEntry(timeEntry)"
                                            [tooltip]="'Zeiteintrag bearbeiten'"
                                            [icon]="'icon-edit'">
                                        </nx-default-button>   
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="flex justify-end mt-1">
                    <nx-default-button
                        [style]="'dark'"
                        (onClick)="createTimeEntry()"
                        [label]="'Zeiteintrag hinzufügen'"
                        [tooltip]="'Zeiteintrag hinzufügen'"
                        [icon]="'icon-plus'">
                    </nx-default-button>    
                    </div>
                </div>
            </p-overlayPanel>

        </div>
    </div>
  `,
    styles: [`
        .nx-edit-dateTime-input-v2{
            .previewValue{
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
        }
    .time-entries-table{
        font-size: 14px;
    }
    .time-entries-table tbody {
        display: block;
        max-height: 200px; /* fixe Höhe */
        overflow-y: auto; /* scrollbar */
    }
    .time-entries-table thead, .time-entries-table tbody tr {
        display: table;
        width: 100%;
        table-layout: fixed;
        text-align: left;
        font-weight: 400;
    }
  `]
})
export class EditTimeEntriesInputComponent implements OnInit {

    dialogRef: DynamicDialogRef | undefined;

    @ViewChild('overlayPanel') overlayPanel: OverlayPanel;
    overlayPanelVisible: boolean = false;
    _categoryOptions = [
        { id: '11', name: 'Anfahrt' },
        { id: '22', name: 'Bedieneinsatz' },
        { id: '33', name: 'Abfahrt' },
      ];
    dateTimePeriod: any;
    timeOptions: string[] = this.generateTimeOptions();
    isOverlayPanelDismissable = true;

    @Input() label: string;
    @Input() showLabel = true;
    @Input() placeholder: string;
    @Input() task: Task;
    @Output() refetch = new EventEmitter<boolean>();

    constructor(
        public dialogService: EditDialogService,
        public confirmDialogService: ConfirmDialogService,
        public timeEntryService: TimeEntryService
    ) {
    }

    ngOnInit() {

    }

    /**
     * Returns a string in that form: 10 Einträge, 5:30 h
     * @returns 
     */
    buildTimeEntryString() {
        if(this.task == undefined || this.task.timeEntries == undefined || this.task.timeEntries.length == 0){
            return undefined;
        }
        // count time entries
        let count = this.task.timeEntries.length;
        // calcualte xxh yymin from time entries sum (seconds)
        let seconds = this.task.timeEntriesSum;
        let hours = Math.floor(seconds / 3600);
        let minutes = Math.floor((seconds % 3600) / 60);
        return `${count} Einträge, ${hours}:${minutes} h`;
    }

    toggleOverlayPanel($event: MouseEvent) {
        this.overlayPanel.toggle($event)
    }

    onBlockOverlayDismiss() {
        this.overlayPanel.unbindDocumentClickListener();
    }

    onUnblockOverlayDismiss() {
        setTimeout(() => {
            this.overlayPanel.bindDocumentClickListener();
        }, 1);
    }

    generateTimeOptions(): string[] {
        const options: string[] = [];
        for (let hour = 0; hour < 8; hour++) {
          for (let minute = 0; minute < 60; minute += 30) {
            const hourStr = hour.toString().padStart(2, '0');
            const minuteStr = minute.toString().padStart(2, '0');
            const timeStr = `${hourStr}:${minuteStr}`;
            options.push(timeStr);
          }
        }
        return options;
    }

    onTimeBlur(event: any, formControlName: string) {
        // let value = this.calendarFormGroup.get(formControlName)?.value;
        // if (!value) return;
        
        // // Handle numeric input like "1300"
        // if (/^\d+$/.test(value)) {
        //     const timeString = value.padStart(4, '0');
        //     const hours = timeString.substring(0, 2);
        //     const minutes = timeString.substring(2, 4);
            
        //     // Validate hours and minutes
        //     if (parseInt(hours) < 24 && parseInt(minutes) < 60) {
        //     value = `${hours}:${minutes}`;
        //     }
        //     this.calendarFormGroup.get(formControlName)?.setValue(value);
        // }
    }

    onTimeFocus(event: any) {
        event.target.select();
    }

    createTimeEntry() {
        this.overlayPanel.unbindDocumentClickListener();
        this.dialogRef = this.dialogService.open(TimeEntryDialogDialogComponent, {
            header: 'Zeiteintrag erstellen',
            additionalClass: 'time-entry-dialog',
            data: {
                taskId: this.task?.id
            }
        });
        this.dialogRef.onClose.subscribe((result) => {
            setTimeout(() => {
                this.overlayPanel.bindDocumentClickListener();
            });
            // result is true or false, indiciating if it should refetch
            if(result){
                this.refetch.emit(true);
            }
        });
    }

    updateTimeEntry(timeEntry: TimeEntry) {
        this.overlayPanel.unbindDocumentClickListener();
        this.dialogRef = this.dialogService.open(TimeEntryDialogDialogComponent, {
            header: 'Zeiteintrag bearbeiten',
            additionalClass: 'time-entry-dialog',
            data: {
                taskId: this.task?.id,
                timeEntry: timeEntry
            }
        });
        this.dialogRef.onClose.subscribe((result) => {
            setTimeout(() => {
                this.overlayPanel.bindDocumentClickListener();
            });
            // result is true or false, indiciating if it should refetch
            if(result){
                this.refetch.emit(true);
            }
        });
    }

    deleteTimeEntry(timeEntry: TimeEntry) {
        this.overlayPanel.unbindDocumentClickListener();
        const message = 'Möchtest Du diesen Zeiteintrag löschen?';
        this.dialogRef = this.confirmDialogService.open({
            message: message,
            severity: ConfirmDialogSeverity.WARN,
            header: 'Zeiteintrag löschen'
        });
        this.dialogRef.onClose.subscribe( (result : ConfirmDialogEventType) => {
            this.overlayPanel.unbindDocumentClickListener();
            if(result === ConfirmDialogEventType.CONFIRM){
                this.timeEntryService.deleteTimeEntry(timeEntry.id, (result: any) => {
                    this.refetch.emit(true);
                });
            };
        })
    }
}